/**
 * LOP 3 / 3 - CHU THICH (SVG, tu chuyen sang canvas khi qua dong).
 *
 * SVG la lua chon dung cho chu thich: moi dau la mot node, nen bat su kien
 * chuot, keo tha, va truy cap bang ban phim deu la chuyen mac dinh. Nhung moi
 * node SVG ton khoang mot kilobyte bo nho va mot muc trong cay bo cuc; qua vai
 * tram node thi moi lan cuon deu keo theo mot lan tinh lai bo cuc.
 *
 * Nguong o day la 500 dau tren mot trang. Tren nguong do ta ve tat ca vao mot
 * canvas duy nhat: mat kha nang tuong tac tung dau, doi lay viec cuon van muot.
 * Trang co hon 500 dau gan nhu luon la ket qua cua cong cu tu dong (danh dau
 * hang loat, lop OCR), khong phai thu nguoi dung se sua tay tung cai.
 *
 * =============================================================================
 *  HINH HOC LAY TU MOT NGUON DUY NHAT
 * =============================================================================
 *  Moi phep tinh hinh hoc o day - hinh chu nhat sinh ra tu mot cu keo, hop bao,
 *  vi tri tay nam, phep bam trung - deu goi core/annotations/shapes.ts, dung cac
 *  ham ma DUONG GHI goi. Khong co mot phep tinh thu hai o tang giao dien.
 *
 *  Do khong phai su sach se ve kien truc, do la dieu kien de ban xem truoc noi
 *  that: mot lan truoc day ban xem truoc va duong ghi tinh rieng, chung troi
 *  khoi nhau, va nguoi dung tin vao ban xem truoc sai trong hai vong lam viec.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { Annotation, PdfPoint, PdfRect } from '@pdfeditor/types';
import { cssPointToPdf, pdfRectToCss, type PageGeometry } from './geometry';
import { smoothStroke } from '../core/annotations/smooth';
import { loadBundledFont, onBundledFontLoaded, peekBundledFont } from '../core/fonts/bundled';
import {
  DEFAULT_TEXT_COLOR_RGB,
  DEFAULT_TEXT_SIZE_PT,
  NOTE_FILL_RGB,
  bundledFaces,
  faceOf,
  layoutTextAnnotation,
  type FontSource,
} from '../ui/textBoxMetrics';
import {
  getFontFamily,
  normalizeFontFamilyId,
  type FontFamilyId,
} from '../core/fonts/families';
import {
  HANDLE_CURSOR,
  handleIdsFor,
  MIN_HIT_TOLERANCE_PT,
  bboxOf,
  dragToRect,
  endpointsOf,
  fillOf,
  handleAt,
  handlePoints,
  moveRect,
  normalizeRect,
  pickAt,
  resolveResize,
  strokeWidthOf,
  type HandleId,
  type ShapeKind,
} from '../core/annotations/shapes';

/** Tren nguong nay, doi sang ve bang canvas. */
export const SVG_MARK_LIMIT = 500;

/** Canh cua o tay nam tren man hinh, don vi diem anh CSS. */
const HANDLE_SIZE_PX = 8;

/**
 * VIEN CHI TREN MAN HINH - khong bao gio di vao tep.
 *
 * Hop chu tu do khong con ve vien (yeu cau cua nguoi dung), va mot hop chu vua
 * tao chua co chu la mot vung trong suot hoan toan: khong co gi de nhin, khong
 * co gi de nham. Net dut nhat nay hien khi ro chuot len hop, va no chi la mot
 * chi dan cua giao dien - dong hinh dang trong tep khong he co no.
 *
 * LA MOT BIEN CSS, khong phai mot hang so hex. Truoc day cho nay ghi cung mot
 * mau xanh rat nhat, chon de noi len tren nen toi. Tren giay trang no chi dat
 * khoang 2.5:1, tuc la mot goi y ma nguoi dung phai nhin ky moi thay. Vi day
 * la mau CUA GIAO DIEN chu khong phai mau cua tai lieu, no phai doi duoc theo
 * ban mau; --ui-hint trong styles.css giu ca hai gia tri.
 *
 * Dat qua thuoc tinh `style` chu khong qua thuoc tinh `stroke` cua SVG: var()
 * trong thuoc tinh trinh bay cua SVG khong duoc bao dam o moi trinh duyet, con
 * trong khai bao CSS thi co.
 */
const SCREEN_HINT_STROKE = 'var(--ui-hint)';

/**
 * Che do bat thao tac cua lop chu thich.
 *
 * 'select' KHAC 'none': 'none' la lop khong nhan su kien nao (nguoi dung dang
 * lam viec voi van ban ben duoi), con 'select' la duoc phep chon / di chuyen /
 * doi kich thuoc doi tuong da co nhung khong tao doi tuong moi.
 */
export type AnnotationCapture = 'none' | 'select' | 'ink' | 'note' | ShapeKind | 'image';

export interface ShapeStyle {
  colorRgb: readonly [number, number, number];
  strokeWidthPt: number;
  fillRgb: readonly [number, number, number] | null;
  opacity: number;
}

export interface AnnotationLayerProps {
  annotations: readonly Annotation[];
  geometry: PageGeometry;
  /** Hop bao cua khoi van ban dang chon - ve khung nhan manh. */
  highlightRect?: { left: number; top: number; width: number; height: number } | null;
  onSelect?: (id: string) => void;

  // --- Bat thao tac ve ------------------------------------------------------
  /** Trang nay la trang thu may. Di kem moi net ve / ghi chu phat ra. */
  pageIndex?: number;
  capture?: AnnotationCapture;
  /** Mot net da ve xong, DA LAM MUOT, toa do PDF. */
  onInkStroke?: (pageIndex: number, path: PdfPoint[]) => void;
  /** Vi tri dat mot ghi chu dinh moi, toa do PDF. */
  onNotePoint?: (pageIndex: number, point: PdfPoint) => void;
  /** Mau / do day cua net dang ve - chi de ve ban xem truoc luc keo chuot. */
  inkColorCss?: string;

  // --- Hinh ve va anh -------------------------------------------------------
  /**
   * Mot hinh da ve xong. `rect` DA di qua dragToRect(), tuc la da ap rang buoc
   * Shift va da o dung quy uoc cua tung loai (vecto cho duong thang / mui ten).
   */
  onShape?: (pageIndex: number, kind: ShapeKind, rect: PdfRect) => void;
  /**
   * Nguoi dung da chi cho dat anh.
   *
   * `rect` la null khi nguoi dung chi BAM mot cai thay vi keo - luc do ben goi
   * dat anh o kich thuoc tu nhien cua no, lay diem bam lam goc tren-trai.
   */
  onImageRect?: (pageIndex: number, rect: PdfRect | null, point: PdfPoint) => void;
  /** Kieu ve cua cong cu dang chon - chi de ve ban xem truoc luc keo. */
  shapeStyle?: ShapeStyle;

  // --- Chon va thao tac -----------------------------------------------------
  selectedId?: string | null;
  /** Bo chon (bam ra nen trong, hoac Esc). */
  onDeselect?: () => void;
  /** Doi hinh chu nhat cua mot doi tuong: keo di cho, hoac keo tay nam. */
  onChangeRect?: (id: string, rect: PdfRect) => void;
  /** Xoa doi tuong dang chon (phim Delete / Backspace). */
  onDelete?: (id: string) => void;
  /**
   * Dia chi hien thi cua mot anh da ghi nhan.
   *
   * Lop nay khong giu byte anh: no chi biet ma tra cuu nam trong chu thich, va
   * hoi ben goi mot dia chi de ve len man hinh. Byte song trong ImageStore.
   */
  imageUrl?: (ref: string) => string | undefined;
}

function rgb(color: readonly [number, number, number]): string {
  return `rgb(${Math.round(color[0] * 255)}, ${Math.round(color[1] * 255)}, ${Math.round(color[2] * 255)})`;
}

/** Cong cu dang bat co tao ra hinh ve khong. */
function shapeKindOf(capture: AnnotationCapture): ShapeKind | null {
  return capture === 'square' || capture === 'circle' || capture === 'line' || capture === 'arrow'
    ? capture
    : null;
}

export function AnnotationLayer({
  annotations,
  geometry,
  highlightRect,
  onSelect,
  pageIndex = 0,
  capture = 'none',
  onInkStroke,
  onNotePoint,
  inkColorCss = '#e0483d',
  onShape,
  onImageRect,
  shapeStyle,
  selectedId,
  onDeselect,
  onChangeRect,
  onDelete,
  imageUrl,
}: AnnotationLayerProps) {
  const useCanvas = annotations.length > SVG_MARK_LIMIT;
  const selected = useMemo(
    () => annotations.find((a) => a.id === selectedId) ?? null,
    [annotations, selectedId],
  );

  return (
    <>
      {useCanvas ? (
        <AnnotationCanvas annotations={annotations} geometry={geometry} />
      ) : (
        <svg className="annotationLayer" xmlns="http://www.w3.org/2000/svg" role="presentation">
          {annotations.map((a) => (
            <AnnotationShape
              key={a.id}
              annotation={a}
              geometry={geometry}
              onSelect={onSelect}
              imageUrl={imageUrl}
            />
          ))}
          {highlightRect && (
            <rect
              className="blockHighlight"
              x={highlightRect.left}
              y={highlightRect.top}
              width={highlightRect.width}
              height={highlightRect.height}
              rx={2}
            />
          )}
        </svg>
      )}
      {capture !== 'none' && (
        <CaptureSurface
          mode={capture}
          pageIndex={pageIndex}
          geometry={geometry}
          annotations={annotations}
          selected={selected}
          onInkStroke={onInkStroke}
          onNotePoint={onNotePoint}
          onShape={onShape}
          onImageRect={onImageRect}
          onSelect={onSelect}
          onDeselect={onDeselect}
          onChangeRect={onChangeRect}
          onDelete={onDelete}
          inkColorCss={inkColorCss}
          {...(shapeStyle ? { shapeStyle } : {})}
        />
      )}
    </>
  );
}

/**
 * =============================================================================
 *  BE MAT BAT THAO TAC
 * =============================================================================
 *
 *  Mot lop RIENG nam tren cac dau da co, chi ton tai khi mot cong cu dang bat.
 *  Ly do khong nhap chung vao lop dau: lop dau de tung dau tu nhan su kien
 *  (de bam vao mot ghi chu de mo no ra), con lop nay phai NUOT toan bo su kien
 *  tren ca trang. Tron hai vai tro vao mot the SVG se cho ra hanh vi "ve duoc o
 *  cho trong nhung khong ve duoc de len mot dau da co" - mot loi rat kho ta.
 *
 *  Su kien duoc CHAN LAI (stopPropagation): the .pageView cha dung
 *  onPointerDown de chon khoi van ban, va mot net ve tay khong duoc dong thoi
 *  la mot lan chon khoi.
 */

/** Thao tac keo dang dien ra. */
type Drag =
  | { kind: 'ink' }
  | { kind: 'draw'; shape: ShapeKind; start: PdfPoint; current: PdfPoint; constrain: boolean }
  | { kind: 'image'; start: PdfPoint; current: PdfPoint; moved: boolean }
  | { kind: 'move'; id: string; origin: PdfRect; grab: PdfPoint; current: PdfPoint }
  | { kind: 'resize'; id: string; handle: HandleId; keepAspect: boolean; current: PdfPoint };

function CaptureSurface({
  mode,
  pageIndex,
  geometry,
  annotations,
  selected,
  onInkStroke,
  onNotePoint,
  onShape,
  onImageRect,
  onSelect,
  onDeselect,
  onChangeRect,
  onDelete,
  inkColorCss,
  shapeStyle,
}: {
  mode: AnnotationCapture;
  pageIndex: number;
  geometry: PageGeometry;
  annotations: readonly Annotation[];
  selected: Annotation | null;
  onInkStroke?: (pageIndex: number, path: PdfPoint[]) => void;
  onNotePoint?: (pageIndex: number, point: PdfPoint) => void;
  onShape?: (pageIndex: number, kind: ShapeKind, rect: PdfRect) => void;
  onImageRect?: (pageIndex: number, rect: PdfRect | null, point: PdfPoint) => void;
  onSelect?: (id: string) => void;
  onDeselect?: () => void;
  onChangeRect?: (id: string, rect: PdfRect) => void;
  onDelete?: (id: string) => void;
  inkColorCss: string;
  shapeStyle?: ShapeStyle;
}) {
  /** Net dang ve, toa do CSS. Chi de hien ban xem truoc. */
  const [live, setLive] = useState<{ x: number; y: number }[]>([]);
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const pointsRef = useRef<PdfPoint[]>([]);
  const hostRef = useRef<SVGSVGElement | null>(null);

  /**
   * Phong dong goi - CAN cho viec doi kich thuoc mot hop chu.
   *
   * Chieu cao hop chu duoc DO ra tu noi dung, khong phai keo ra (xem
   * resolveResize). Do phep do do can font, va no phai la CUNG mot font ma
   * duong ghi dung, neu khong thi hop tren man hinh va hop trong tep se lech.
   *
   * Nguon mat chu phai theo DUNG HO FONT cua doi tuong dang chon: moi ho co be
   * rong glyph rieng, nen do lai chieu cao mot hop Noto Serif bang so do Noto
   * Sans se cho ra mot hop sai (bai hoc so 4).
   */
  const baseFont = useBundledFaces();
  const font = useMemo(
    () => familyFontSource(baseFont, normalizeFontFamilyId(selected?.fontFamily)),
    [baseFont, selected?.fontFamily],
  );

  const setDragBoth = (next: Drag | null) => {
    dragRef.current = next;
    setDrag(next);
  };

  /**
   * DUNG SAI BAM, quy ve DON VI TRANG.
   *
   * Chia cho zoom la mau chot: o muc thu nho 50%, bon diem anh tren man hinh
   * chi la hai diem cua trang. Dung sai co dinh theo don vi trang se thanh ra
   * kho bam dan khi nguoi dung thu nho, dung luc ho can no rong nhat.
   */
  const tolerance = Math.max(MIN_HIT_TOLERANCE_PT, HANDLE_SIZE_PX / Math.max(0.1, geometry.zoom));

  const localPoint = (event: ReactPointerEvent<SVGSVGElement>) => {
    const host = event.currentTarget.getBoundingClientRect();
    return { px: event.clientX - host.left, py: event.clientY - host.top };
  };

  const handleDown = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const { px, py } = localPoint(event);
      const pdf = cssPointToPdf(px, py, geometry);

      // CHAN SU KIEN chi khi lop nay THUC SU nhan lay no.
      //
      // O che do 'select', mot cu bam vao cho trong khong thuoc ve lop nay: the
      // .pageView cha dung chinh cu bam do de chon khoi van ban ben duoi. Chan
      // vo dieu kien o day se lam cong cu Chọn mat kha nang chon chu - lop chu
      // thich trong suot bong nhien nuot ca trang.
      const passive = mode === 'select';
      if (!passive) event.stopPropagation();

      if (mode === 'note') {
        onNotePoint?.(pageIndex, pdf);
        return;
      }

      // Bat con tro: neu nguoi dung keo ra ngoai mep trang roi nha chuot, ta
      // van nhan duoc pointerup. Khong bat thi thao tac se "dinh" lai mai mai.
      //
      // Boc try/catch chu khong de nem: setPointerCapture nem NotFoundError khi
      // con tro khong con hoat dong (con tro bien mat giua hai khung hinh, hoac
      // su kien duoc phat ra bang ma). Mat viec bat con tro chi lam thao tac keo
      // ra ngoai mep bi hut; de ngoai le thoat ra thi thao tac KHONG BAO GIO bat dau.
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // khong bat duoc con tro - van lam viec binh thuong
      }

      const shape = shapeKindOf(mode);
      if (shape) {
        event.preventDefault();
        setDragBoth({ kind: 'draw', shape, start: pdf, current: pdf, constrain: event.shiftKey });
        return;
      }

      if (mode === 'image') {
        event.preventDefault();
        setDragBoth({ kind: 'image', start: pdf, current: pdf, moved: false });
        return;
      }

      if (mode === 'ink') {
        event.preventDefault();
        setDragBoth({ kind: 'ink' });
        pointsRef.current = [pdf];
        setLive([{ x: px, y: py }]);
        return;
      }

      // --- Che do chon ------------------------------------------------------
      // Tay nam duoc thu TRUOC phep bam trung doi tuong: tay nam nam DE LEN mep
      // hinh, nen mot cu bam vao goc luon trung ca hai. Thu doi tuong truoc thi
      // tay nam se khong bao gio bam duoc, va nguoi dung chi di chuyen duoc hinh
      // chu khong bao gio doi duoc kich thuoc.
      if (selected) {
        const handle = handleAt(bboxOf(selected), pdf, tolerance, selected.kind);
        if (handle) {
          event.preventDefault();
          event.stopPropagation();
          setDragBoth({
            kind: 'resize',
            id: selected.id,
            handle,
            /*
             * SHIFT CHI CO MOT NGHIA: GIU NGUYEN TI LE.
             *
             * Truoc day Shift dao nghia theo loai doi tuong - voi hinh ve no GIU
             * ti le, voi anh no BO ti le. Tung ben deu co ly rieng, nhung mot
             * phim bo tro doi nghia giua hai loai doi tuong la mot cai bay: nguoi
             * dung hoc mot lan roi dung sai o lan sau.
             *
             * Quy tac moi, khong bao gio dao:
             *   hinh ve            khong phim   -> tu do
             *   hinh ve  + Shift                -> giu ti le
             *   anh                khong phim   -> giu ti le (mac dinh dung, keo
             *                                     gian mot buc anh la thay ngay)
             *   anh      + Shift                -> van giu ti le
             *   anh      + Alt                  -> bop meo, phai noi ro y dinh
             */
            keepAspect:
              (selected.kind === 'stamp' && !event.altKey) || event.shiftKey,
            current: pdf,
          });
          return;
        }
      }

      const hit = pickAt(annotations, pdf, tolerance);
      if (!hit) {
        // Bam ra cho trong: bo chon, VA de su kien di tiep xuong trang - cu bam
        // do con la mot lan chon khoi van ban.
        onDeselect?.();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (hit.id !== selected?.id) onSelect?.(hit.id);
      setDragBoth({ kind: 'move', id: hit.id, origin: hit.rect, grab: pdf, current: pdf });
    },
    [mode, geometry, pageIndex, onNotePoint, annotations, selected, tolerance, onSelect, onDeselect],
  );

  const handleMove = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const active = dragRef.current;
      if (!active) return;
      event.stopPropagation();
      const { px, py } = localPoint(event);
      const pdf = cssPointToPdf(px, py, geometry);

      switch (active.kind) {
        case 'ink':
          // AP LUC BUT (event.pressure) BI BO QUA CO Y - /Ink cua PDF chi co mot
          // be rong net cho ca duong. Xem core/annotations/smooth.ts.
          pointsRef.current.push(pdf);
          setLive((prev) => [...prev, { x: px, y: py }]);
          break;
        case 'draw':
          setDragBoth({ ...active, current: pdf, constrain: event.shiftKey });
          break;
        case 'image':
          setDragBoth({
            ...active,
            current: pdf,
            // "Da di chuyen" quyet dinh bam-de-dat-co-tu-nhien hay keo-de-dat-vua-khung.
            // Nguong tinh theo don vi trang de no khong doi y nghia theo muc thu phong.
            moved:
              active.moved ||
              Math.hypot(pdf.x - active.start.x, pdf.y - active.start.y) > MIN_HIT_TOLERANCE_PT,
          });
          break;
        case 'move':
          setDragBoth({ ...active, current: pdf });
          break;
        case 'resize':
          setDragBoth({ ...active, current: pdf, keepAspect: active.keepAspect });
          break;
      }
    },
    [geometry],
  );

  const finish = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const active = dragRef.current;
      if (!active) return;
      event.stopPropagation();
      setDragBoth(null);

      switch (active.kind) {
        case 'ink': {
          const raw = pointsRef.current;
          pointsRef.current = [];
          setLive([]);
          if (raw.length < 2) return;
          onInkStroke?.(pageIndex, smoothStroke(raw));
          return;
        }
        case 'draw': {
          const rect = dragToRect(active.start, active.current, active.shape, active.constrain);
          // Mot cu bam khong keo cho ra hinh rong. Ghi no ra se la mot doi tuong
          // vo hinh trong tep, nen ta bo qua thay vi tao rac.
          if (Math.abs(rect.width) < 1 && Math.abs(rect.height) < 1) return;
          onShape?.(pageIndex, active.shape, rect);
          return;
        }
        case 'image': {
          if (!active.moved) {
            onImageRect?.(pageIndex, null, active.start);
            return;
          }
          const rect = dragToRect(active.start, active.current, 'square', false);
          onImageRect?.(pageIndex, rect, active.start);
          return;
        }
        case 'move': {
          const next = moveRect(
            active.origin,
            active.current.x - active.grab.x,
            active.current.y - active.grab.y,
          );
          if (next.x !== active.origin.x || next.y !== active.origin.y) {
            onChangeRect?.(active.id, next);
          }
          return;
        }
        case 'resize': {
          if (!selected || selected.id !== active.id) return;
          // DUNG ham ma o chon vua ve ban xem truoc bang (SelectionFrame goi
          // resolveResize voi dung bo tham so nay). Neu cho nay tinh rieng, hinh
          // se NHAY mot cai vao dung luc nguoi dung nha tay - va do la khoanh
          // khac te nhat de lo ra rang hai phep tinh khong khop.
          const next = resolveResize(selected, active.handle, active.current, {
            keepAspect: active.keepAspect,
            font,
          });
          const before = selected.rect;
          // Keo hut mot chut ma khong doi gi thi khong ghi lenh: mot buoc hoan
          // tac khong lam gi ca la mot buoc Ctrl+Z bi "nuot".
          if (
            next.x === before.x &&
            next.y === before.y &&
            next.width === before.width &&
            next.height === before.height
          ) {
            return;
          }
          onChangeRect?.(active.id, next);
          return;
        }
      }
    },
    [onInkStroke, pageIndex, onShape, onImageRect, onChangeRect, selected, font],
  );

  /**
   * Phim tat cua doi tuong dang chon.
   *
   * Gan tren `window` chu khong tren the SVG: mot the SVG khong nhan duoc tieu
   * diem ban phim, nen mot trinh nghe cuc bo se khong bao gio chay. Trinh nghe
   * TU BO QUA khi tieu diem dang nam trong mot o nhap lieu - nguoi dung dang go
   * chu vao mot o ghi chu ma bam Backspace la muon xoa mot ky tu, khong phai xoa
   * ca doi tuong.
   */
  useEffect(() => {
    if (!selected) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT');
      if (typing) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        onDeselect?.();
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        onDelete?.(selected.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, onDelete, onDeselect]);

  const livePath =
    live.length >= 2
      ? live.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
      : null;

  const cursor =
    mode === 'select' ? 'default' : mode === 'note' ? 'copy' : 'crosshair';

  return (
    <svg
      ref={hostRef}
      className={`annotationLayer annotationCapture${mode === 'note' ? ' noteCapture' : ''}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ cursor }}
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={finish}
      onPointerCancel={finish}
    >
      {livePath && (
        <path
          d={livePath}
          fill="none"
          stroke={inkColorCss}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {drag?.kind === 'draw' && (
        <DrawPreview drag={drag} geometry={geometry} style={shapeStyle} />
      )}
      {drag?.kind === 'image' && drag.moved && (
        <BoxPreview
          rect={dragToRect(drag.start, drag.current, 'square', false)}
          geometry={geometry}
        />
      )}
      {selected && (
        <SelectionFrame annotation={selected} geometry={geometry} drag={drag} font={font} />
      )}
    </svg>
  );
}

/**
 * BAN XEM TRUOC LUC DANG KEO.
 *
 * Ve tu CHINH hinh chu nhat ma dragToRect() se giao cho duong ghi, khong phai
 * tu toa do chuot tho. Neu hai cho tinh rieng thi rang buoc Shift se hien mot
 * dang khi keo va ghi ra mot dang khac khi nha tay.
 */
function DrawPreview({
  drag,
  geometry,
  style,
}: {
  drag: { shape: ShapeKind; start: PdfPoint; current: PdfPoint; constrain: boolean };
  geometry: PageGeometry;
  style?: ShapeStyle;
}) {
  const rect = dragToRect(drag.start, drag.current, drag.shape, drag.constrain);
  const preview: Annotation = {
    id: '__preview__',
    kind: drag.shape,
    pageIndex: 0,
    rect,
    colorRgb: style?.colorRgb ?? [0.88, 0.28, 0.24],
    opacity: style?.opacity ?? 1,
    createdAtMs: 0,
    ...(style ? { strokeWidthPt: style.strokeWidthPt, fillRgb: style.fillRgb } : {}),
  };
  return <ShapeGraphic annotation={preview} geometry={geometry} />;
}

/** Khung dut net cho vung dat anh dang keo. */
function BoxPreview({ rect, geometry }: { rect: PdfRect; geometry: PageGeometry }) {
  const r = pdfRectToCss(normalizeRect(rect), geometry);
  return (
    <rect
      className="dragPreviewBox"
      x={r.left}
      y={r.top}
      width={r.width}
      height={r.height}
      strokeWidth={1}
      strokeDasharray="5 4"
    />
  );
}

/**
 * O CHON: khung bao va tam tay nam.
 *
 * Tay nam duoc ve trong khi keo NUA, va di theo hinh chu nhat dang thay doi -
 * mot o chon dung yen trong luc hinh dang co gian la mot ban xem truoc noi doi
 * o quy mo nho.
 */
function SelectionFrame({
  annotation,
  geometry,
  drag,
  font,
}: {
  annotation: Annotation;
  geometry: PageGeometry;
  drag: Drag | null;
  font: FontSource | null;
}) {
  let rect = annotation.rect;
  if (drag?.kind === 'move' && drag.id === annotation.id) {
    rect = moveRect(drag.origin, drag.current.x - drag.grab.x, drag.current.y - drag.grab.y);
  } else if (drag?.kind === 'resize' && drag.id === annotation.id) {
    rect = resolveResize(annotation, drag.handle, drag.current, {
      keepAspect: drag.keepAspect,
      font,
    });
  }

  const box = normalizeRect(rect);
  const frame = pdfRectToCss(box, geometry);
  const points = handlePoints(box);
  const half = HANDLE_SIZE_PX / 2;

  return (
    <g className="annotationSelection" pointerEvents="none">
      {/* Mau lay tu styles.css (.selectionOutline / .selectionHandle) de o chon
          doi theo ban mau - xem ghi chu o SCREEN_HINT_STROKE. */}
      <rect
        className="selectionOutline"
        x={frame.left}
        y={frame.top}
        width={frame.width}
        height={frame.height}
        strokeWidth={1}
        strokeDasharray="4 3"
      />
      {/*
        Chi ve nhung tay nam THAT SU keo duoc cho loai doi tuong nay. Ghi chu va
        hop chu co chieu cao suy ra tu noi dung nen khong co tay nam tren / duoi
        - ve chung ra roi tu choi trong im lang la moi goi mot thao tac khong bao
        gio xay ra. Xem handleIdsFor().
      */}
      {handleIdsFor(annotation.kind).map((id) => {
        const p = points[id];
        const c = pdfRectToCss({ x: p.x, y: p.y, width: 0, height: 0 }, geometry);
        return (
          <rect
            key={id}
            className="selectionHandle"
            x={c.left - half}
            y={c.top - half}
            width={HANDLE_SIZE_PX}
            height={HANDLE_SIZE_PX}
            strokeWidth={1.5}
            style={{ cursor: HANDLE_CURSOR[id] }}
          />
        );
      })}
    </g>
  );
}

/**
 * VE MOT HINH - dung chung giua doi tuong that va ban xem truoc.
 *
 * Mot ham cho ca hai la co y: neu ban xem truoc co doan ve rieng, hinh hien ra
 * luc keo se khac hinh hien ra sau khi nha tay, va nguoi dung se thay no "nhay".
 */
function ShapeGraphic({
  annotation,
  geometry,
  imageUrl,
}: {
  annotation: Annotation;
  geometry: PageGeometry;
  imageUrl?: (ref: string) => string | undefined;
}) {
  const box = bboxOf(annotation);
  const r = pdfRectToCss(box, geometry);
  const color = rgb(annotation.colorRgb);
  const fill = fillOf(annotation);
  const fillCss = fill ? rgb(fill) : 'none';
  // Be rong net doi tu diem cua trang sang diem anh man hinh qua muc thu phong:
  // mot net 3pt phai DAY LEN khi nguoi dung phong to, dung nhu no se day len khi
  // in ra o kho lon hon.
  const strokeWidth = Math.max(0.5, strokeWidthOf(annotation) * geometry.zoom);

  switch (annotation.kind) {
    case 'square':
      return (
        <rect
          x={r.left}
          y={r.top}
          width={r.width}
          height={r.height}
          fill={fillCss}
          stroke={color}
          strokeWidth={strokeWidth}
        />
      );
    case 'circle':
      return (
        <ellipse
          cx={r.left + r.width / 2}
          cy={r.top + r.height / 2}
          rx={r.width / 2}
          ry={r.height / 2}
          fill={fillCss}
          stroke={color}
          strokeWidth={strokeWidth}
        />
      );
    case 'line':
    case 'arrow': {
      const { from, to } = endpointsOf(annotation.rect);
      const a = pdfRectToCss({ x: from.x, y: from.y, width: 0, height: 0 }, geometry);
      const b = pdfRectToCss({ x: to.x, y: to.y, width: 0, height: 0 }, geometry);
      const head =
        annotation.kind === 'arrow'
          ? arrowHeadPath(a.left, a.top, b.left, b.top, Math.max(6, strokeWidth * 4))
          : null;
      return (
        <g>
          <line
            x1={a.left}
            y1={a.top}
            x2={b.left}
            y2={b.top}
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          {head && (
            <path
              d={head}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </g>
      );
    }
    case 'stamp': {
      const href = annotation.imageRef ? imageUrl?.(annotation.imageRef) : undefined;
      if (!href) {
        // Chua co dia chi anh (vd trang vua duoc nap lai): ve mot o cho ro rang
        // chu khong ve gi ca. Mot cho trong se lam nguoi dung tuong anh da mat.
        return (
          <rect
            x={r.left}
            y={r.top}
            width={r.width}
            height={r.height}
            fill="none"
            stroke={color}
            strokeDasharray="5 4"
            strokeWidth={1}
          />
        );
      }
      return (
        <image
          href={href}
          x={r.left}
          y={r.top}
          width={r.width}
          height={r.height}
          preserveAspectRatio="none"
        />
      );
    }
    default:
      return null;
  }
}

/** Hai net cheo o dau mui ten, toa do CSS. Cung goc mo voi duong ghi. */
function arrowHeadPath(x1: number, y1: number, x2: number, y2: number, size: number): string {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const spread = Math.PI / 7;
  const lx = x2 - size * Math.cos(angle - spread);
  const ly = y2 - size * Math.sin(angle - spread);
  const rx = x2 - size * Math.cos(angle + spread);
  const ry = y2 - size * Math.sin(angle + spread);
  return `M ${lx} ${ly} L ${x2} ${y2} L ${rx} ${ry}`;
}

function AnnotationShape({
  annotation,
  geometry,
  onSelect,
  imageUrl,
}: {
  annotation: Annotation;
  geometry: PageGeometry;
  onSelect?: (id: string) => void;
  imageUrl?: (ref: string) => string | undefined;
}) {
  const r = pdfRectToCss(bboxOf(annotation), geometry);
  const color = rgb(annotation.colorRgb);
  const common = {
    opacity: annotation.opacity,
    onPointerDown: () => onSelect?.(annotation.id),
    style: { cursor: onSelect ? 'pointer' : 'default' },
  } as const;

  switch (annotation.kind) {
    case 'underline':
      return (
        <line
          {...common}
          x1={r.left}
          y1={r.top + r.height}
          x2={r.left + r.width}
          y2={r.top + r.height}
          stroke={color}
          strokeWidth={Math.max(1, r.height * 0.06)}
        />
      );
    case 'strikeout':
      return (
        <line
          {...common}
          x1={r.left}
          y1={r.top + r.height / 2}
          x2={r.left + r.width}
          y2={r.top + r.height / 2}
          stroke={color}
          strokeWidth={Math.max(1, r.height * 0.06)}
        />
      );
    case 'squiggly': {
      const step = Math.max(3, r.height * 0.25);
      const baseY = r.top + r.height;
      let d = `M ${r.left} ${baseY}`;
      for (let x = r.left; x < r.left + r.width; x += step) {
        d += ` q ${step / 2} ${-step / 2} ${step} 0`;
      }
      return <path {...common} d={d} fill="none" stroke={color} strokeWidth={1.2} />;
    }
    case 'square':
    case 'circle':
    case 'line':
    case 'arrow':
    case 'stamp':
      return (
        <g {...common}>
          <ShapeGraphic annotation={annotation} geometry={geometry} {...(imageUrl ? { imageUrl } : {})} />
        </g>
      );
    case 'ink': {
      const paths = annotation.inkPaths ?? [];
      return (
        <g {...common}>
          {paths.map((path, i) => {
            if (path.length === 0) return null;
            const d = path
              .map((p, idx) => {
                const c = pdfRectToCss(
                  { x: p.x, y: p.y, width: 0, height: 0 },
                  geometry,
                );
                return `${idx === 0 ? 'M' : 'L'} ${c.left} ${c.top}`;
              })
              .join(' ');
            return (
              <path
                key={i}
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={Math.max(1, strokeWidthOf(annotation) * geometry.zoom)}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}
        </g>
      );
    }
    case 'text-note':
      // BIEU TUONG chi khi duoc yeu cau ro. Mac dinh la hop doc duoc, ve y het
      // hop chu - do la ca diem cua thay doi nay.
      return annotation.noteStyle === 'icon' ? (
        <g {...common}>
          <rect x={r.left} y={r.top} width={18} height={18} rx={3} fill={color} />
          <title>{annotation.contents ?? 'Ghi chú'}</title>
        </g>
      ) : (
        <TextBoxGraphic annotation={annotation} geometry={geometry} common={common} />
      );
    case 'free-text':
      return <TextBoxGraphic annotation={annotation} geometry={geometry} common={common} />;
    default:
      return null;
  }
}

/**
 * HOP CHU TREN MAN HINH.
 *
 * Ve tu CHINH phep do ma duong ghi dung (layoutTextAnnotation). Khong co font
 * thi ve khung dut net kem dau hoi: mot hop dac khong chu se trong nhu da xong
 * trong khi thuc ra chua do duoc gi.
 */
/**
 * HO MAT CHU DONG GOI cho lop hien thi.
 *
 * `peekBundledFont()` truoc: App da nap ban Regular tu luc mo tai lieu, nen
 * thuong co ngay va khong co lan ve nao thieu chu. Chi khi chua co moi cho.
 *
 * VI SAO CO ONBUNDLEDFONTLOADED: mat chu dam / nghieng duoc tai LUOI - chi khi
 * co mot doi tuong that su can den. Trong nhip ve dau tien sau khi nguoi dung
 * bam "Đậm", tep NotoSans-Bold.ttf con dang tren duong, va phep do lui ve ban
 * Regular. Khong co tin hieu nay thi React khong co ly do gi de ve lai, va hop
 * chu se dung mai so do cua Regular: chu dam ma khong nhin dam, dung dieu nguoi
 * dung dang phan nan.
 */
function useBundledFaces(): FontSource | null {
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    const off = onBundledFontLoaded(() => setGeneration((n) => n + 1));
    void loadBundledFont();
    return off;
  }, []);
  const regular = peekBundledFont()?.font ?? null;
  return useMemo(
    () => (regular ? bundledFaces(regular) : null),
    // `generation` co trong danh sach phu thuoc mot cach co chu dich: mot mat
    // chu moi nap xong khong doi `regular`, nhung no PHAI cho ra mot nguon mat
    // chu moi, neu khong useMemo cua hop chu ben duoi se giu lai bo cuc cu.
    [regular, generation],
  );
}

/**
 * NGUON MAT CHU THEO DUNG HO FONT DA CHON.
 *
 * Dung DUNG ham ma duong ghi dung: bundledFaces(fallback, familyId) trong
 * ui/textBoxMetrics.ts da chon mat chu theo ho font. Goi lai chinh no o day, chu
 * KHONG go mot ban thu hai cua phep chon mat chu - hai ban se troi khoi nhau va
 * do dung la bai hoc so 3 o quy mo mat chu.
 *
 * `base` la nguon ho mac dinh tu useBundledFaces(): faceOf(base, {}) rut ra ban
 * Regular ho mac dinh, dung lam duong lui khi mat chu cua ho duoc chon chua nap
 * xong. `base` doi lai moi khi mot mat chu moi nap xong (useBundledFaces phu
 * thuoc `generation`), nen ca cay ve lai bang so do that khi tep font ve toi noi.
 */
export function familyFontSource(
  base: FontSource | null,
  familyId: FontFamilyId,
): FontSource | null {
  if (!base) return null;
  return bundledFaces(faceOf(base, {}), familyId);
}

function TextBoxGraphic({
  annotation,
  geometry,
  common,
}: {
  annotation: Annotation;
  geometry: PageGeometry;
  common: { opacity: number; onPointerDown: () => void; style: { cursor: string } };
}) {
  const base = useBundledFaces();
  const [hovered, setHovered] = useState(false);
  const familyId = normalizeFontFamilyId(annotation.fontFamily);
  const faces = useMemo(() => familyFontSource(base, familyId), [base, familyId]);
  const laid = useMemo(
    // CUNG ham ma duong ghi goi, va - qua `faces` - CUNG mat chu (VA ho font) ma
    // duong ghi se nhung. Dam / nghieng / phong chu tren man hinh va trong tep la
    // mot quyet dinh, khong phai hai.
    () => (faces ? layoutTextAnnotation(faces, annotation) : null),
    [faces, annotation],
  );

  /*
   * KHONG VIEN cho hop chu tu do - DUNG quy tac ma textBoxAppearance() dung.
   *
   * Hai cho phai thay doi cung nhau, va do la ly do quy tac o day duoc viet ra
   * thanh mot dong duy nhat de doi chieu duoc voi ban trong core: doi mot ben
   * ma quen ben kia thi ban xem truoc noi doi, va do la bai hoc so 3.
   */
  const bordered = annotation.kind !== 'free-text';

  const stroke = rgb(annotation.colorRgb);
  if (!laid) {
    const r = pdfRectToCss(bboxOf(annotation), geometry);
    return (
      <rect
        {...common}
        x={r.left}
        y={r.top}
        width={Math.max(r.width, 8)}
        height={Math.max(r.height, 8)}
        fill="none"
        // Chua do duoc hop (font chua nap xong) - khung dut net nay la trang
        // thai "chua xong", khong phai vien cua doi tuong. Hop chu tu do dung
        // mau chi dan cua giao dien de no khong bi doc nham thanh mot vien that.
        //
        // Mau CUA TAI LIEU van di qua thuoc tinh `stroke`; mau CUA GIAO DIEN di
        // qua `style` vi no la mot bien CSS. Khai bao style dat sau khi trai
        // `common` de khong nuot mat con tro chuot cua no.
        stroke={bordered ? stroke : undefined}
        strokeDasharray="4 3"
        strokeWidth={1}
        style={bordered ? common.style : { ...common.style, stroke: SCREEN_HINT_STROKE }}
      />
    );
  }

  const box = pdfRectToCss(laid.rect, geometry);
  const fill =
    annotation.fillRgb === null
      ? 'none'
      : rgb(annotation.fillRgb ?? (annotation.kind === 'text-note' ? NOTE_FILL_RGB : [1, 1, 1]));
  const textColor = rgb(annotation.textColorRgb ?? DEFAULT_TEXT_COLOR_RGB);
  const sizePx = (annotation.fontSizePt ?? DEFAULT_TEXT_SIZE_PT) * geometry.zoom;

  return (
    <g {...common} onPointerEnter={() => setHovered(true)} onPointerLeave={() => setHovered(false)}>
      <rect
        x={box.left}
        y={box.top}
        width={box.width}
        height={box.height}
        rx={4 * geometry.zoom}
        // `fill="none"` KHONG nhan su kien chuot; mot mau trong suot thi co.
        // Khac biet nay quyet dinh mot hop chu rong con bam duoc hay khong -
        // khong co no, hop khong vien khong nen thanh mot doi tuong khong
        // ton tai duoi con tro.
        fill={bordered || fill !== 'none' ? fill : 'transparent'}
        // Vien THAT (mau cua tai lieu) qua thuoc tinh; goi y chi tren man hinh
        // qua `style`, vi no la mot bien CSS - xem SCREEN_HINT_STROKE.
        stroke={bordered ? stroke : undefined}
        {...(bordered
          ? {}
          : {
              strokeDasharray: '3 3',
              style: { stroke: hovered ? SCREEN_HINT_STROKE : 'none' },
            })}
        strokeWidth={
          bordered ? Math.max(1, strokeWidthOf(annotation) * geometry.zoom) : hovered ? 1 : 0
        }
      />
      {laid.metrics.placedLines.map((line, i) => (
        <text
          key={i}
          // Toa do dong = goc hop + do lech da do. `baselineYPt` dem TU DAY hop
          // len (he PDF), con y cua SVG dem tu dinh xuong - nen phai lat o day.
          x={box.left + line.xPt * geometry.zoom}
          y={box.top + box.height - line.baselineYPt * geometry.zoom}
          fill={textColor}
          // Ho font CUA DOI TUONG, lay tu so dang ky (core/fonts/families.ts).
          // cssStack luon dat ten @font-face dong goi ('PdfEditorNoto',
          // 'PdfEditorNotoSerif', 'PdfEditorBeVietnam') len DAU - moi ten do khai
          // bao du BON mat trong styles.css va tro thang vao chinh bon tep .ttf se
          // duoc nhung vao PDF. Phai dat ten dong goi truoc, KHONG phai 'Noto
          // Sans': 'Noto Sans' chi trung khi may nguoi dung tinh co da cai san
          // font do; may khong co thi ca dong chu tut xuong font he thong, va man
          // hinh ve mot mat chu trong khi tep mang mat chu khac (bai hoc so 3).
          fontFamily={getFontFamily(annotation.fontFamily).cssStack}
          fontSize={sizePx}
          // VI TRI dong da do bang mat chu that (xem `faces` o tren); hai thuoc
          // tinh nay chi lam cho HINH DANG glyph tren man hinh khop voi hinh
          // dang se duoc ghi ra. Thieu chung thi hop nhay rong ra dung be rong
          // cua chu dam trong khi glyph van gay - trong nhu mot loi gian dong.
          fontWeight={annotation.bold ? 700 : 400}
          fontStyle={annotation.italic ? 'italic' : 'normal'}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {line.text}
        </text>
      ))}
    </g>
  );
}

/**
 * Duong di so luong lon: mot canvas, mot lan ve, khong node DOM nao.
 * Ve o devicePixelRatio de duong net khong bi rang cua.
 */
function AnnotationCanvas({
  annotations,
  geometry,
}: {
  annotations: readonly Annotation[];
  geometry: PageGeometry;
}) {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssW = parent.clientWidth;
    const cssH = parent.clientHeight;
    canvas.width = Math.max(1, Math.floor(cssW * dpr));
    canvas.height = Math.max(1, Math.floor(cssH * dpr));
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    for (const a of annotations) {
      const r = pdfRectToCss(bboxOf(a), geometry);
      ctx.globalAlpha = a.opacity;
      const color = rgb(a.colorRgb);
      const width = Math.max(1, strokeWidthOf(a) * geometry.zoom);
      switch (a.kind) {
        case 'underline':
        case 'strikeout':
          ctx.strokeStyle = color;
          ctx.lineWidth = Math.max(1, r.height * 0.06);
          ctx.beginPath();
          {
            const y = a.kind === 'underline' ? r.top + r.height : r.top + r.height / 2;
            ctx.moveTo(r.left, y);
            ctx.lineTo(r.left + r.width, y);
          }
          ctx.stroke();
          break;
        case 'ink':
          ctx.strokeStyle = color;
          ctx.lineWidth = width;
          ctx.lineCap = 'round';
          for (const path of a.inkPaths ?? []) {
            ctx.beginPath();
            path.forEach((p, idx) => {
              const c = pdfRectToCss({ x: p.x, y: p.y, width: 0, height: 0 }, geometry);
              if (idx === 0) ctx.moveTo(c.left, c.top);
              else ctx.lineTo(c.left, c.top);
            });
            ctx.stroke();
          }
          break;
        case 'square':
        case 'stamp': {
          const fill = fillOf(a);
          if (fill) {
            ctx.fillStyle = rgb(fill);
            ctx.fillRect(r.left, r.top, r.width, r.height);
          }
          ctx.strokeStyle = color;
          ctx.lineWidth = width;
          ctx.strokeRect(r.left, r.top, r.width, r.height);
          break;
        }
        case 'circle': {
          const fill = fillOf(a);
          ctx.beginPath();
          ctx.ellipse(
            r.left + r.width / 2,
            r.top + r.height / 2,
            Math.max(0, r.width / 2),
            Math.max(0, r.height / 2),
            0,
            0,
            Math.PI * 2,
          );
          if (fill) {
            ctx.fillStyle = rgb(fill);
            ctx.fill();
          }
          ctx.strokeStyle = color;
          ctx.lineWidth = width;
          ctx.stroke();
          break;
        }
        case 'line':
        case 'arrow': {
          const { from, to } = endpointsOf(a.rect);
          const p0 = pdfRectToCss({ x: from.x, y: from.y, width: 0, height: 0 }, geometry);
          const p1 = pdfRectToCss({ x: to.x, y: to.y, width: 0, height: 0 }, geometry);
          ctx.strokeStyle = color;
          ctx.lineWidth = width;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(p0.left, p0.top);
          ctx.lineTo(p1.left, p1.top);
          ctx.stroke();
          break;
        }
        case 'free-text':
          // Hop chu tu do khong co vien - o day cung vay. Duong canvas khong ve
          // chu (no ton tai de cuon muot tren trang hang tram dau), nen ve mot
          // khung o day se la ve DUNG cai thu da bo di va khong ve thu duy nhat
          // con lai.
          break;
        default:
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(r.left, r.top, r.width, r.height);
          break;
      }
    }
    ctx.globalAlpha = 1;
  }, [annotations, geometry]);

  return <canvas className="annotationLayer annotationLayerCanvas" ref={ref} />;
}
